/*
 * [VALE] Predictive search dropdown: real product results + term highlighting.
 *
 * WHY THIS EXISTS
 * ---------------
 * /search/suggest is answered by Shopify's semantic search. It ignores the
 * theme's `resources[options][fields]` restriction and returns loosely
 * associated products - searching "rubber" put P-clips above the rubber hose
 * range, and even "zzzqqx" returned hits. The full /search page uses the
 * keyword engine and ranks correctly.
 *
 * So the dropdown keeps using /search/suggest for the suggestions, collections
 * and pages column (that part is good), but the PRODUCTS come from
 * /search?q=<term>&type=product&view=psr - templates/search.psr.liquid - which
 * is the same engine and ranking as the full search page. That response also
 * carries the grand total, so one request covers the cards and every
 * [data-psr-count] slot.
 *
 * It also highlights the search term in product titles; the API only provides
 * styled_text for query suggestions, never for product titles.
 *
 * Loaded from sections/header.liquid, not from the search section:
 * predictive-search.js injects that section with innerHTML, and scripts
 * inserted that way never execute.
 */
(function () {
  'use strict';

  var cache = {};
  var inflight = {};

  function searchUrl() {
    return (window.routes && window.routes.search_url) || '/search';
  }

  /* Wrap case-insensitive matches of the term in <mark>. Built from text nodes
     rather than innerHTML so a product title can never inject markup. */
  function highlight(root) {
    var term = (root.getAttribute('data-psr-term') || '').trim().toLowerCase();
    if (!term) return;

    var titles = root.querySelectorAll('.psr-card__title:not([data-psr-hl])');
    for (var t = 0; t < titles.length; t++) {
      var el = titles[t];
      el.setAttribute('data-psr-hl', '');

      var text = el.textContent;
      var lower = text.toLowerCase();
      var i = lower.indexOf(term);
      if (i === -1) continue;

      var frag = document.createDocumentFragment();
      var pos = 0;
      while (i !== -1) {
        if (i > pos) frag.appendChild(document.createTextNode(text.slice(pos, i)));
        var mark = document.createElement('mark');
        mark.className = 'psr-hl';
        mark.textContent = text.slice(i, i + term.length);
        frag.appendChild(mark);
        pos = i + term.length;
        i = lower.indexOf(term, pos);
      }
      if (pos < text.length) frag.appendChild(document.createTextNode(text.slice(pos)));

      el.textContent = '';
      el.appendChild(frag);
    }
  }

  function fetchResults(term) {
    if (cache[term]) return Promise.resolve(cache[term]);
    if (inflight[term]) return inflight[term];

    var url = searchUrl() + '?q=' + encodeURIComponent(term) + '&type=product&view=psr';
    inflight[term] = fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var payload = doc.querySelector('[data-psr-payload]');
        if (!payload) throw new Error('no payload');
        var shownEl = doc.querySelector('[data-psr-shown-value]');
        var parsed = {
          items: payload.innerHTML,
          total: parseInt(payload.getAttribute('data-psr-total'), 10) || 0,
          shown: shownEl ? parseInt(shownEl.textContent.trim(), 10) || 0 : 0
        };
        cache[term] = parsed;
        delete inflight[term];
        return parsed;
      })
      .catch(function (err) {
        delete inflight[term];
        throw err;
      });

    return inflight[term];
  }

  function showFailure(root) {
    var skeleton = root.querySelector('[data-psr-skeleton]');
    var searching = root.querySelector('[data-psr-searching]');
    var failed = root.querySelector('[data-psr-failed]');
    if (skeleton) skeleton.remove();
    if (searching) searching.remove();
    if (failed) failed.removeAttribute('hidden');
  }

  function fill(root) {
    var list = root.querySelector('[data-psr-products]');
    if (!list || root.getAttribute('data-psr-filled')) return;

    var term = root.getAttribute('data-psr-term') || '';
    if (!term) return;

    root.setAttribute('data-psr-filled', '1');

    fetchResults(term)
      .then(function (data) {
        if (!data.shown) {
          showFailure(root);
          return;
        }

        list.innerHTML = data.items;
        list.removeAttribute('hidden');

        var skeleton = root.querySelector('[data-psr-skeleton]');
        if (skeleton) skeleton.remove();

        var searching = root.querySelector('[data-psr-searching]');
        if (searching) searching.remove();

        var shownSlots = root.querySelectorAll('[data-psr-shown]');
        for (var s = 0; s < shownSlots.length; s++) shownSlots[s].textContent = String(data.shown);

        var countSlots = root.querySelectorAll('[data-psr-count]');
        for (var c = 0; c < countSlots.length; c++) countSlots[c].textContent = String(data.total);

        var reveal = root.querySelectorAll('[data-psr-count-hide]');
        for (var r = 0; r < reveal.length; r++) reveal[r].removeAttribute('hidden');

        highlight(root);
      })
      .catch(function () {
        showFailure(root);
      });
  }

  /* Advert carousel under the product grid.
   *
   * The whole results block is re-rendered by predictive-search.js on every
   * keystroke, so a fresh carousel node arrives constantly and the old one is
   * detached with its timer still running. Rather than tracking nodes, each
   * tick checks whether its own element is still in the document and clears
   * its interval if not - a detached carousel costs at most one stale tick.
   */
  function initCarousels(root) {
    var carousels = root.querySelectorAll('[data-psr-carousel]:not([data-psr-carousel-init])');

    for (var c = 0; c < carousels.length; c++) {
      (function (el) {
        el.setAttribute('data-psr-carousel-init', '1');

        var slides = el.querySelectorAll('[data-psr-slide]');
        var dots = el.querySelectorAll('[data-psr-dot]');
        if (slides.length < 2) return;

        var index = 0;
        var paused = false;
        var stopped = false;
        var delay = parseInt(el.getAttribute('data-psr-interval'), 10) || 5000;

        function show(next) {
          index = (next + slides.length) % slides.length;
          for (var i = 0; i < slides.length; i++) {
            var on = i === index;
            slides[i].classList.toggle('is-active', on);
            /* Inactive slides are visibility:hidden, so they are already out
               of the accessibility tree - but the attribute keeps the DOM
               honest for anything reading it directly. */
            if (on) slides[i].removeAttribute('aria-hidden');
            else slides[i].setAttribute('aria-hidden', 'true');

            if (dots[i]) {
              dots[i].classList.toggle('is-active', on);
              if (on) dots[i].setAttribute('aria-current', 'true');
              else dots[i].removeAttribute('aria-current');
            }
          }
        }

        for (var d = 0; d < dots.length; d++) {
          (function (i) {
            dots[i].addEventListener('click', function (event) {
              /* The dots sit inside the panel, not inside a slide, but stop
                 the event anyway so a click can never be read as a click on
                 the advert link behind it. */
              event.preventDefault();
              event.stopPropagation();
              stopped = true; /* reader has taken control - stop rotating */
              show(i);
            });
          })(d);
        }

        el.addEventListener('mouseenter', function () { paused = true; });
        el.addEventListener('mouseleave', function () { paused = false; });
        el.addEventListener('focusin', function () { paused = true; });
        el.addEventListener('focusout', function () { paused = false; });

        /* Reduced motion removes the CROSSFADE, not the rotation - the CSS
           media query drops the transition so slides swap instantly. Skipping
           auto-advance here instead left the banner frozen on advert 1 for
           anyone with Windows animations turned off, which reads as a broken
           carousel rather than an accessibility courtesy. Hover, focus and the
           dots remain the way to stop it. */
        var timer = setInterval(function () {
          if (!document.contains(el)) {
            clearInterval(timer);
            return;
          }
          if (paused || stopped) return;
          show(index + 1);
        }, delay);
      })(carousels[c]);
    }
  }

  function enhance() {
    var roots = document.querySelectorAll('[data-psr-root]');
    for (var i = 0; i < roots.length; i++) {
      fill(roots[i]);
      highlight(roots[i]);
      initCarousels(roots[i]);
    }
  }

  /* Results are swapped in wholesale via innerHTML, so watch for that rather
     than hooking predictive-search.js. Our own edits re-trigger the observer
     once; the data-psr-filled / data-psr-hl guards make that pass a no-op. */
  function observe() {
    var instances = document.querySelectorAll('predictive-search');
    for (var i = 0; i < instances.length; i++) {
      var ps = instances[i];
      if (ps.getAttribute('data-psr-observed')) continue;
      ps.setAttribute('data-psr-observed', '1');
      new MutationObserver(enhance).observe(ps, { childList: true, subtree: true });
    }
    enhance();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
